import React, { createContext, useContext, useState, useEffect } from 'react';
import { getTenantFromToken } from '../services/authApi.js';

// Development flag for mock authentication
const USEMOCK_AUTH = import.meta.env.VITE_USE_MOCK_AUTH === 'true';

interface TenantContextType {
  tenant_id: string;
  tenant: string;
  setTenantId: (id: string) => void;
}

const TenantContext = createContext<TenantContextType | null>(null);

export function getTenantId(): string {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];
  
  // Handle development environment
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'brain';
  }

  // Development brain instance: dev-brain.astrapeak.ai
  if (subdomain === 'dev-brain') {
    return 'brain';
  }
  
  // Customer tenants: e.g., prospance-brain.astrapeak.ai, acme-brain.astrapeak.ai
  if (subdomain.endsWith('-brain')) {
    return subdomain.replace('-brain', '');
  }
  
  // Legacy fallback: Main brain instance (if needed)
  if (subdomain === 'br') {
    return 'brain';
  }
  
  // Future feature: Multi-region support
  // const [tenant, region] = subdomain.split('-');
  // if (region) {
  //   return `${tenant}-${region}`;
  // }
  
  return 'unknown';
}

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize tenant_id from JWT token, localStorage, or fallback to getTenantId()
  const [tenant_id, setTenantIdState] = useState<string>(() => {
    // First try to get tenant from JWT token if available
    const token = localStorage.getItem('astrapeak_auth_token');
    if (token) {
      const tokenTenant = getTenantFromToken(token);
      if (tokenTenant) {
        return tokenTenant;
      }
    }
    
    // Then try localStorage
    const storedTenantId = localStorage.getItem('tenant_id');
    if (storedTenantId) {
      return storedTenantId;
    }
    
    // Finally fallback to subdomain detection
    return getTenantId();
  });
  const tenant = tenant_id; // For now, tenant name is same as ID

  // Custom setter that updates both state and localStorage
  const setTenantId = (id: string) => {
    setTenantIdState(id);
    if (id) {
      localStorage.setItem('tenant_id', id);
    } else {
      localStorage.removeItem('tenant_id');
    }
  };

  // Update tenant_id when auth token or localStorage changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'tenant_id') {
        setTenantIdState(event.newValue || getTenantId());
      } else if (event.key === 'astrapeak_auth_token') {
        // If token changes, try to extract tenant from it
        if (event.newValue) {
          const tokenTenant = getTenantFromToken(event.newValue);
          if (tokenTenant) {
            setTenantId(tokenTenant);
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Check for token changes within the current tab
  useEffect(() => {
    // This effect runs once on mount to ensure tenant is set from token
    const token = localStorage.getItem('astrapeak_auth_token');
    if (token) {
      const tokenTenant = getTenantFromToken(token);
      if (tokenTenant && tokenTenant !== tenant_id) {
        setTenantId(tokenTenant);
      }
    }
  }, [tenant_id]);

  return (
    <TenantContext.Provider value={{ tenant_id, tenant, setTenantId }}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantProvider;
